// Alignment while moving objects (owner, 2026-10-20: "I need a way to align objects with centre lines and outer lines.
// When I'm positioning, especially connecting lint pots to pumps and things like that, there needs to be some snapping
// if there are aligning centre lines").
//
// A drag (Select tool) asks alignMove for the move to use. In this order:
//   1. CONNECT  - an end (port) of a moving component lands on a FREE end of another component or pipe, the two ends
//                 facing each other. Exact.
//   2. CENTRE LINE - an end of a moving component lands on the centre line of another end: a pump flange, a fitting
//                 end, a lint pot end or a pipe (the pipe's centre line). The two lines are parallel. Then the other
//                 direction can also line up on a second, square centre line, or on an edge (4).
//   3. (the Select tool's normal point snap: corners, ends, middles)
//   4. EDGES    - the box of the moving objects lines up with the box of another object: left, centre or right;
//                 top, middle or bottom.
// Pure: the tool gathers the ends and boxes once at the start of the drag (they do not change while it moves).
import type { Vec } from "../core/geometry";
import type { Port } from "../pipes/api";

export interface Box {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export interface AlignInput {
  /** Ends of the moving components, where they are at the start of the drag. */
  movingPorts: Port[];
  /** The box of everything that moves, at the start of the drag. */
  movingBox: Box | null;
  /** Ends of everything that does not move (a pipe that follows a moving component is left out). */
  targetPorts: Port[];
  /** Boxes of the objects that do not move. */
  targetBoxes: Box[];
  /** A name for an owner, for the hint. */
  nameOf: (ownerId: string) => string;
}

export interface AlignGuide {
  a: Vec;
  b: Vec;
  kind: "connect" | "axis" | "edge";
}

export interface AlignResult {
  delta: Vec;
  guides: AlignGuide[];
  hint: string | null;
}

const dot = (a: Vec, b: Vec) => a.x * b.x + a.y * b.y;
const cross = (a: Vec, b: Vec) => a.x * b.y - a.y * b.x;
const add = (a: Vec, b: Vec): Vec => ({ x: a.x + b.x, y: a.y + b.y });
const sub = (a: Vec, b: Vec): Vec => ({ x: a.x - b.x, y: a.y - b.y });
const PARALLEL = Math.sin((2 * Math.PI) / 180);
const FACING = -Math.cos((5 * Math.PI) / 180);

/** The axis-aligned direction a line constrains: "y" for a level line, "x" for an upright one, null otherwise. */
function constrains(u: Vec): "x" | "y" | null {
  if (Math.abs(u.y) < PARALLEL) return "y";
  if (Math.abs(u.x) < PARALLEL) return "x";
  return null;
}

/**
 * The move to use. `raw` = the cursor's move with no snap. `snapped` = the move after the normal point snap, or null
 * when the point snap found nothing. `tol` = how near counts, in drawing mm (a few screen pixels).
 */
export function alignMove(input: AlignInput, raw: Vec, snapped: Vec | null, tol: number): AlignResult {
  const { movingPorts, targetPorts } = input;

  // 1. connect: end on a free end, facing
  let best: { d: number; delta: Vec; mp: Port; tp: Port } | null = null;
  for (const mp of movingPorts) {
    const at = add(mp.p, raw);
    for (const tp of targetPorts) {
      if (tp.taken || dot(mp.dir, tp.dir) > FACING) continue;
      const d = Math.hypot(at.x - tp.p.x, at.y - tp.p.y);
      if (d <= tol && (!best || d < best.d)) best = { d, delta: sub(tp.p, mp.p), mp, tp };
    }
  }
  if (best) {
    return {
      delta: best.delta,
      guides: [{ a: best.tp.p, b: best.tp.p, kind: "connect" }],
      hint: `Connects: the ${best.mp.label} meets the ${best.tp.label} of ${input.nameOf(best.tp.ownerId)} (${best.tp.size} mm). Release to place.`
    };
  }

  // 2. centre lines: an end on the centre line of another end (parallel lines)
  const onAxis = (delta: Vec, skip?: Vec): { off: number; mp: Port; tp: Port; nrm: Vec } | null => {
    let found: { off: number; mp: Port; tp: Port; nrm: Vec } | null = null;
    for (const mp of movingPorts) {
      const at = add(mp.p, delta);
      for (const tp of targetPorts) {
        if (Math.abs(cross(mp.dir, tp.dir)) > PARALLEL) continue;
        if (skip && Math.abs(dot(skip, tp.dir)) > PARALLEL) continue; // the second line must be square to the first
        const nrm = { x: -tp.dir.y, y: tp.dir.x };
        const off = dot(sub(at, tp.p), nrm);
        if (Math.abs(off) <= tol && (!found || Math.abs(off) < Math.abs(found.off))) found = { off, mp, tp, nrm };
      }
    }
    return found;
  };
  const first = movingPorts.length ? onAxis(raw) : null;
  if (first) {
    let delta = sub(raw, { x: first.nrm.x * first.off, y: first.nrm.y * first.off });
    const guides: AlignGuide[] = [{ a: first.tp.p, b: add(first.mp.p, delta), kind: "axis" }];
    const words = [`the centre line of the ${first.tp.label} of ${input.nameOf(first.tp.ownerId)}`];
    const second = onAxis(delta, first.tp.dir);
    let free: "x" | "y" | null = constrains(first.tp.dir) === "y" ? "x" : constrains(first.tp.dir) === "x" ? "y" : null;
    if (second) {
      delta = sub(delta, { x: second.nrm.x * second.off, y: second.nrm.y * second.off });
      guides.push({ a: second.tp.p, b: add(second.mp.p, delta), kind: "axis" });
      words.push(`the centre line of the ${second.tp.label} of ${input.nameOf(second.tp.ownerId)}`);
      free = null;
    }
    if (free) {
      const edge = edgeAlign(input, delta, tol, free);
      if (edge) {
        delta = edge.delta;
        guides.push(...edge.guides);
        words.push(edge.words);
      }
    }
    return { delta, guides, hint: `On ${words.join(" and ")}.` };
  }

  // 3. the normal point snap
  if (snapped) return { delta: snapped, guides: [], hint: null };

  // 4. edges and centres
  let delta = raw;
  const guides: AlignGuide[] = [];
  const words: string[] = [];
  for (const axis of ["x", "y"] as const) {
    const edge = edgeAlign(input, delta, tol, axis);
    if (edge) {
      delta = edge.delta;
      guides.push(...edge.guides);
      words.push(edge.words);
    }
  }
  return { delta, guides, hint: words.length ? `Lined up: ${words.join(", ")}.` : null };
}

const X_NAMES = ["left edge", "centre", "right edge"];
const Y_NAMES = ["top edge", "middle", "bottom edge"];

/** Line up one direction of the moving box with the nearest edge or centre of another box. */
function edgeAlign(input: AlignInput, delta: Vec, tol: number, axis: "x" | "y"): { delta: Vec; guides: AlignGuide[]; words: string } | null {
  const m = input.movingBox;
  if (!m) return null;
  const lines = (b: Box): number[] => (axis === "x" ? [b.minX, (b.minX + b.maxX) / 2, b.maxX] : [b.minY, (b.minY + b.maxY) / 2, b.maxY]);
  const shift = axis === "x" ? delta.x : delta.y;
  const mine = lines(m).map((v) => v + shift);
  let best: { d: number; i: number; j: number; box: Box; value: number } | null = null;
  for (const box of input.targetBoxes) {
    const theirs = lines(box);
    mine.forEach((v, i) =>
      theirs.forEach((w, j) => {
        const d = Math.abs(v - w);
        if (d <= tol && (!best || d < best.d)) best = { d, i, j, box, value: w };
      })
    );
  }
  if (!best) return null;
  const b = best as { d: number; i: number; j: number; box: Box; value: number };
  const fix = b.value - mine[b.i]!;
  const next = axis === "x" ? { x: delta.x + fix, y: delta.y } : { x: delta.x, y: delta.y + fix };
  const moved: Box = { minX: m.minX + next.x, maxX: m.maxX + next.x, minY: m.minY + next.y, maxY: m.maxY + next.y };
  const lo = axis === "x" ? Math.min(moved.minY, b.box.minY) : Math.min(moved.minX, b.box.minX);
  const hi = axis === "x" ? Math.max(moved.maxY, b.box.maxY) : Math.max(moved.maxX, b.box.maxX);
  const guide: AlignGuide = axis === "x" ? { a: { x: b.value, y: lo }, b: { x: b.value, y: hi }, kind: "edge" } : { a: { x: lo, y: b.value }, b: { x: hi, y: b.value }, kind: "edge" };
  const names = axis === "x" ? X_NAMES : Y_NAMES;
  const words = b.i === b.j ? `${names[b.i]} to ${names[b.j]}` : `${names[b.i]} to the ${names[b.j]} of another object`;
  return { delta: next, guides: [guide], words: b.i === b.j ? `${words} of another object` : words };
}
